import { useState } from 'react';
import { Control, FieldError, useController } from 'react-hook-form';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { Grid, InputLabel, Paper, CircularProgress, Stack, Typography } from '@mui/material';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { useDropzone, DropzoneOptions, FileRejection } from 'react-dropzone';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { convertMimeToExt } from '@/utils/mimeToExt';
import { IWalletUploadedDocument } from '@/types/types/forms/wallet/wallet.interface';
import { WalletUploadedFilesList } from '@/components/blocks/wallet/Payment/WalletUploadedFilesList/WalletUploadedFilesList';
import { FormErrorText } from '@/components/forms/other/ErrorText/FormErrorText';

import { UploadFileIcon } from '@/assets/theme/default/general/UploadFileIcon';

export const WALLET_FILE_UPLOAD_DEFAULT_OPTIONS = {
    supportedFormats: ['application/pdf'],
    maxFileCount: 1,
    maxFileSizeMB: 20,
};
interface WalletFileUploadProps {
    id: string;
    control: Control;
    documents?: Record<string, IWalletUploadedDocument[]>;
    updatePaymentDocumentsById?: (id: string, newDocuments: IWalletUploadedDocument[]) => void;
    fileFieldPhraseId?: number;
    labelPhraseId?: number;
    required?: boolean;
    maxFileSizeMB?: number;
    maxFileCount?: number;
    supportedFormats?: string[];
    error?: FieldError | null;
}

export const WalletFileUpload = ({
    id,
    control,
    documents,
    updatePaymentDocumentsById,
    fileFieldPhraseId,
    labelPhraseId,
    required,
    maxFileSizeMB: _maxFileSizeMB,
    maxFileCount: _maxFileCount,
    supportedFormats,
    error,
}: WalletFileUploadProps) => {
    const { t } = usePhrase();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const minFileSizeBytes = 100; // prevent all empty or near-empty files from being uploaded
    const maxFileSizeMB = _maxFileSizeMB || WALLET_FILE_UPLOAD_DEFAULT_OPTIONS.maxFileSizeMB;
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    const maxFileCount = _maxFileCount || WALLET_FILE_UPLOAD_DEFAULT_OPTIONS.maxFileCount;
    const allowedMimeTypes =
        supportedFormats || WALLET_FILE_UPLOAD_DEFAULT_OPTIONS.supportedFormats;

    const allowedFileExtensions = convertMimeToExt(allowedMimeTypes);

    const currentDocuments = documents?.[id] || [];

    const {
        field: { onChange },
    } = useController({
        name: id,
        control,
    });

    const onDrop: DropzoneOptions['onDrop'] = (
        acceptedFiles: File[],
        fileRejections: FileRejection[],
    ) => {
        setIsUploading(true);

        const setErrorAndStopUploading = (
            errorPhrase: number,
            replaceTextVars?: (string | number)[],
        ) => {
            setErrorMessage(t(errorPhrase, { replaceTextVars, asString: true }));
            setIsUploading(false);
        };

        if (acceptedFiles.length + currentDocuments.length > maxFileCount) {
            setErrorAndStopUploading(WALLET_PHRASES.UPLOAD_MORE_THAN_ALLOWED, [maxFileCount]);
            return;
        }

        const isFileSizeValid = (file: File) =>
            file.size >= minFileSizeBytes && file.size <= maxFileSizeBytes;

        const allFiles = [...acceptedFiles, ...fileRejections.map((rejection) => rejection.file)];
        const invalidFile = allFiles.find((file) => !isFileSizeValid(file));

        if (invalidFile) {
            const errorMessage =
                invalidFile.size > maxFileSizeBytes
                    ? WALLET_PHRASES.YOU_CAN_UPLOAD_FILES_UP_TO
                    : WALLET_PHRASES.FILE_CAN_NOT_BE_EMPTY;

            const errorParams = invalidFile.size > maxFileSizeBytes ? [maxFileSizeMB] : undefined;

            setErrorAndStopUploading(errorMessage, errorParams);
            return;
        }

        if (fileRejections.length > 0) {
            setErrorAndStopUploading(WALLET_PHRASES.UPLOAD_NOT_SUPPORTED, [allowedFileExtensions]);
            return;
        }

        const newDocuments: IWalletUploadedDocument[] = acceptedFiles.map((file) => ({
            file,
            fileFieldPhraseId,
            uploadedAt: new Date().toISOString(),
        }));

        const updatedDocuments = [...currentDocuments, ...newDocuments];

        updatePaymentDocumentsById?.(id, updatedDocuments);
        onChange(updatedDocuments);

        setErrorMessage(null);
        setIsUploading(false);
    };

    const handleDocumentRemove = (id: string, docToRemove: IWalletUploadedDocument) => {
        const updatedDocuments = currentDocuments.filter((doc) => doc.file !== docToRemove.file);
        updatePaymentDocumentsById?.(id, updatedDocuments);
        onChange(updatedDocuments);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: allowedMimeTypes.reduce(
            (acc, format) => {
                acc[format] = [];
                return acc;
            },
            {} as Record<string, string[]>,
        ),
        maxFiles: maxFileCount - currentDocuments.length,
        disabled: isUploading || currentDocuments.length >= maxFileCount,
        maxSize: maxFileSizeBytes,
        minSize: minFileSizeBytes,
    });

    return (
        <Grid container spacing={1}>
            {errorMessage && (
                <Grid size={12} sx={{ mb: 1 }}>
                    <ErrorAlert id="error_message_box" errorMessage={errorMessage} />
                </Grid>
            )}
            {currentDocuments.length < maxFileCount && (
                <>
                    {labelPhraseId && (
                        <Grid size={12} sx={{ mb: 1 }}>
                            <InputLabel required={required} error={!!error}>
                                {t(labelPhraseId, { asString: true })}
                            </InputLabel>
                        </Grid>
                    )}
                    <Grid size={12}>
                        <Paper
                            {...getRootProps()}
                            className={`WalletUploadFileInput ${error ? 'Mui-error' : ''}`}
                            sx={{
                                p: 2,
                                cursor: isUploading ? 'not-allowed' : 'pointer',
                                textAlign: 'center',
                                borderStyle: 'dashed',
                                bgcolor: isDragActive ? 'rgba(0, 0, 0, 0.05)' : 'inherit',
                            }}
                        >
                            <input {...getInputProps()} data-testid="wallet-upload-file-input" />
                            {!isUploading && (
                                <Stack
                                    sx={{
                                        alignItems: 'center',
                                        alignContent: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <UploadFileIcon />
                                    <Typography variant="body1">
                                        {t(WALLET_PHRASES.DRAG_AND_DROP_A_FILE_OR_CHOOSE_TO_UPLOAD)}
                                    </Typography>
                                </Stack>
                            )}
                            {isUploading && <CircularProgress data-testid="progress" />}
                        </Paper>
                        <Grid
                            container
                            sx={{
                                alignItems: 'flex-end',
                                justifyContent: 'space-between',
                                mt: 0.5,
                            }}
                        >
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="greyLabel">
                                    <Stack
                                        direction="row"
                                        sx={{
                                            gap: 1,
                                        }}
                                    >
                                        {t(WALLET_PHRASES.SUPPORTED_FORMATS)}
                                        <span>{allowedFileExtensions}</span>
                                    </Stack>
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: ['left', 'right'] }}>
                                <Typography variant="greyLabel">
                                    {t(WALLET_PHRASES.MAXIMUM_SIZE_MB, {
                                        replaceTextVars: [maxFileSizeMB],
                                        asString: true,
                                    })}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    {error && (
                        <Grid size={12}>
                            <FormErrorText error={error} />
                        </Grid>
                    )}
                </>
            )}
            {currentDocuments.length > 0 && (
                <WalletUploadedFilesList
                    documents={documents}
                    handleDocumentRemove={handleDocumentRemove}
                />
            )}
        </Grid>
    );
};
